/**
 * `POST /api/paypal-orders/preview` — server-authoritative checkout total.
 *
 * The wallet sheet (Apple/Google Pay) displays `grandTotal` as the amount the
 * buyer confirms. Pinning the schema closes the class of bug the route was
 * built to prevent (PR #219 Finding #10): client-controlled display amount
 * vs. server-charged amount divergence. Consumer-protection / regulatory
 * surface — the response IS the contract per the route's own header doc.
 *
 * Fields are intentionally minimal: adding line-item or fee-breakdown keys
 * reopens the attack surface this endpoint was created to close. Keep the
 * shape narrow; if a new wallet flow needs more, design a new endpoint.
 *
 * Request shape: reuses `CheckoutIntent` from `checkout-intent.ts` posted as
 * `{ intent }`. No dedicated request schema here — the route uses
 * `checkIntentShape(body.intent)` like the create route does.
 *
 * @see app/api/paypal-orders/preview/route.ts — handler
 * @see .tondocs/payments/CUTOVER_STEP_3_REVIEW.md Finding #10
 * @see .tondocs/payments/API_RESPONSE_AUDIT.md §3
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface PreviewOrderResponse {
  /** Server-resolved order total. Currency-formatted by the caller. */
  grandTotal: number;
  /** ISO-4217 (e.g. "USD", "EUR", "JPY"). */
  currency: string;
  /** True for JPY/TWD/HUF — caller uses integer formatting (no decimals). */
  zeroDecimal: boolean;
}

export const PreviewOrderResponseSchema = pinned<PreviewOrderResponse>()(
  z.object({
    grandTotal: z.number(),
    currency: z.string().min(1),
    zeroDecimal: z.boolean(),
  }),
);
